/**
 * Recruiter-facing video interview page.
 *
 * On mount we POST to `/video/interviews/{id}/room` which (idempotently)
 * provisions the 100ms room and returns:
 *   - host_join_url:        Prebuilt URL to embed for the recruiter
 *   - candidate_join_url:   Magic link to copy/send to the candidate
 *
 * The room is then rendered inside an `<iframe>`.
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Loader2, Link as LinkIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import VideoInterviewRoom from "@/components/VideoInterviewRoom";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const VideoInterviewHostPage = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [room, setRoom] = useState(null);
  const [copied, setCopied] = useState(false);

  useDocumentTitle(
    room?.candidate_name
      ? `🎥 Entrevista com ${room.candidate_name}`
      : "Sala de entrevista",
  );

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setLoading(true);
      setError("");
      try {
        const r = await axios.post(`${API}/video/interviews/${interviewId}/room`);
        if (cancelled) return;
        setRoom(r.data);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.detail ||
            "Não foi possível inicializar a sala de vídeo."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [interviewId]);

  const copyLink = async () => {
    if (!room?.candidate_join_url) return;
    try {
      await navigator.clipboard.writeText(room.candidate_join_url);
      setCopied(true);
      toast.success("Link do candidato copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Preparando sala segura no 100ms…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 px-6 text-center">
        <div className="max-w-md">
          <h1 className="heading text-xl font-bold text-white mb-2">
            Não foi possível iniciar a entrevista por vídeo
          </h1>
          <p className="text-sm text-slate-400 mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="border-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-950" data-testid="video-host-page">
      <header className="px-4 py-2.5 bg-slate-900 border-b border-slate-700 flex items-center gap-3 flex-wrap">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-slate-300 hover:text-white hover:bg-slate-800"
          data-testid="video-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Sair
        </Button>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-semibold">Entrevista por vídeo</div>
          <div className="text-[11px] text-slate-400 font-mono truncate">
            sala: {room?.hms_room_id}
          </div>
        </div>
        {room?.candidate_join_url && (
          <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 max-w-md">
            <LinkIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs text-slate-300 font-mono truncate flex-1">
              {room.candidate_join_url}
            </span>
            <Button
              size="sm"
              onClick={copyLink}
              className="h-7 px-2 bg-brand-blue hover:bg-brand-blue-dark text-white"
              data-testid="copy-candidate-link"
            >
              {copied ? (
                <CheckCircle2 className="w-3 h-3 mr-1" />
              ) : (
                <Copy className="w-3 h-3 mr-1" />
              )}
              {copied ? "Copiado" : "Copiar link"}
            </Button>
          </div>
        )}
      </header>

      <VideoInterviewRoom joinUrl={room?.host_join_url} />
    </div>
  );
};

export default VideoInterviewHostPage;
